// client/src/components/Footer.tsx
"use client";

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDown, ChevronUp, ArrowRight, Users } from 'lucide-react';
import { Instagram, Linkedin, Twitter, Youtube } from '@/components/icons/BrandIcons';
import { trackEvent } from '@/analytics';
import { useAuth } from '@/contexts/AuthContext';
import quotes from '@/lib/quotes';

interface FooterProps {
  serverAuthState?: {
    user: { id?: string; email?: string; name?: string; token?: string } | null;
    isAuthenticated: boolean;
  };
}

const Footer = ({ serverAuthState }: FooterProps = {}) => {
  const [isMounted, setIsMounted] = useState(false);
  const { user } = useAuth();
  const pathname = usePathname();
  const currentYear = new Date().getFullYear();
  const currentDate = new Date();
  const [_expandedSections, setExpandedSections] = useState<string[]>([]);
  const [showFooterLinks, setShowFooterLinks] = useState(false);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(() => new Date().getDate() - 1);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const [isCoachingButtonVisible, setIsCoachingButtonVisible] = useState(false);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const coachingButtonRef = useRef<HTMLAnchorElement>(null);

  const isAuthenticated = !!user || serverAuthState?.isAuthenticated;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (entry.target === buttonRef.current) {
              setIsButtonVisible(true);
            } else if (entry.target === coachingButtonRef.current) {
              setIsCoachingButtonVisible(true);
            }
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the button is visible
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before fully in view
      }
    );

    if (buttonRef.current) {
      observer.observe(buttonRef.current);
    }
    if (coachingButtonRef.current) {
      observer.observe(coachingButtonRef.current);
    }

    // Capture ref values at effect time to use in cleanup
    const buttonEl = buttonRef.current;
    const coachingButtonEl = coachingButtonRef.current;

    return () => {
      if (buttonEl) {
        observer.unobserve(buttonEl);
      }
      if (coachingButtonEl) {
        observer.unobserve(coachingButtonEl);
      }
    };
  }, []);

  const handleNextQuote = () => {
    setCurrentQuoteIndex((prevIndex) => (prevIndex + 1) % quotes.length);
  };

  const _toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const getUKTaxYearBanner = () => {
    const month = currentDate.getMonth(); // 0-11 (Jan-Dec)
    const day = currentDate.getDate();

    if ((month === 0)) {
      // January
      return {
        title: "Self Assessment Season",
        description: "Don't forget to file your self assessment before 31st January.",
        href: "https://www.gov.uk/log-in-file-self-assessment-tax-return",
        tracking: "self_assessment"
      };
    } else if ((month === 3 && day >= 6) || (month === 4) || (month === 5)) {
      // April 6-30: New tax year, ISA allowance reset
      return {
        title: "New Tax Year Started",
        description: "Your £20,000 ISA allowance has been reset. Start investing for the new tax year.",
        href: "/guides/what-is-an-isa",
        tracking: "new_tax_year"
      };
    } else if (month === 0 || month === 1 || month === 2 || (month === 3 && day < 6)) {
      // March 1 - April 5: Tax year end urgency
      return {
        title: "Tax Year Deadline",
        description: "Use your £20,000 ISA allowance before April 5th or lose it for this tax year.",
        href: "/guides/what-is-an-isa",
        tracking: "tax_year_deadline"
      };
    } else if (month === 9 || month === 10 || month === 11 || month === 12) {
      // November - December: Year-end planning
      return {
        title: "Year-End Planning",
        description: "Review your finances before the end of the year. Maximize pension contributions and tax efficiency.",
        href: "/guides/what-is-a-sipp",
        tracking: "year_end_planning"
      };
    } else {
      // Rest of year: General wealth building
      return {
        title: "Build Your Wealth",
        description: "Start investing. Make your money work harder with smart financial planning.",
        href: "/guides/how-to-start-investing",
        tracking: "wealth_building"
      };
    }
  };

  const currentBanner = getUKTaxYearBanner();

  const trackFooterClick = (category: string, label: string) => {
    trackEvent('footer_link_click', {
      link_category: category,
      link_label: label
    });
  };

  const trackCTAClick = (ctaType: string) => {
    trackEvent('footer_cta_click', {
      cta_type: ctaType
    });
  };

  const footerColumns = [
    {
      title: 'Company',
      links: [
        { text: 'Why Delphina exists', href: '/why-delphina-exists' },
        { text: 'Pricing', href: '/pricing' },
        { text: 'Blog', href: '/blog' },
        { text: 'Statistics', href: '/stats' },
        { text: 'Research', href: '/research' },
        { text: 'Review Us', href: '/review' },
        { text: 'Press Kit', href: '/press' },
        { text: 'AI Information', href: '/ai-information' }
      ]
    },
    {
      title: 'Product',
      links: [
        { text: 'All solutions', href: '/features' },
        { text: 'Budgeting app', href: '/features/budgeting-app' },
        { text: 'Expense tracking', href: '/features/expense-tracking' },
        { text: 'Scenario planning', href: '/features/scenario-planning' },
        { text: 'House purchase planning', href: '/features/house-purchase-planning' },
        { text: 'Compare Delphina', href: '/comparison' },
        { text: 'Employee Wellbeing', href: '/employee-benefits' },
        { text: 'Feature request', href: '/help/feature-request' }
      ]
    },
    {
      title: 'Help & Support',
      links: [
        { text: 'Help', href: '/help' },
        { text: 'Getting started', href: '/help/getting-started' },
        { text: 'Contact support', href: '/help/contact-support' },
        { text: 'Bug report', href: '/help/bug-report' },
        { text: 'Benefits Calculator', href: 'https://www.entitledto.co.uk/', external: true },
        { text: 'All tools', href: '/tools' },
        { text: 'UK brokers compared', href: '/brokers' },
        { text: 'Pension providers compared', href: '/pension-providers' }
      ]
    },
    {
      title: 'Calculators',
      links: [
        { text: 'Am I on track for retirement?', href: '/are-you-on-track' },
        { text: 'Free UK pension calculator', href: '/pension-calculator' },
        { text: 'When can I retire?', href: '/calculators/when-can-i-retire' },
        { text: 'FIRE calculator UK', href: '/fire-calculator' },
        { text: 'Should I overpay mortgage or save?', href: '/mortgage-vs-savings' },
        { text: 'Savings calculator', href: '/savings-calculator' },
        { text: 'Take-home pay calculator', href: '/take-home-pay' },
        { text: 'Will my pension last?', href: '/will-i-run-out-of-money' }
      ]
    },
    {
      title: 'Guides',
      links: [
        { text: 'How much do I need to retire?', href: '/guides/how-much-do-i-need-to-retire' },
        { text: 'Will my pension be enough?', href: '/guides/will-my-pension-be-enough' },
        { text: 'All guides', href: '/guides' },
        { text: 'Personal finance tips', href: '/guides/personal-finance-tips' },
        { text: 'How to budget', href: '/guides/how-to-budget' },
        { text: "I'm being made redundant", href: '/guides/im-being-made-redundant' },
        { text: 'How to start investing', href: '/guides/how-to-start-investing' },
        { text: 'What is FIRE?', href: '/guides/what-is-fire' }
      ]
    },
    {
      title: 'Legal',
      links: [
        { text: 'Terms & conditions', href: '/terms' },
        { text: 'Privacy policy', href: '/privacy' },
        { text: 'Security', href: '/security' },
        { text: 'Cookies', href: '/cookies' },
        { text: 'Glossary', href: '/glossary' },
        { text: 'Creator fund', href: '/affiliates' }
      ]
    }
  ];

  return (
    <footer className="bg-black text-white border-t-2 border-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Sign-up CTA Section - Only show if user is not logged in and not on login page */}
        {!isAuthenticated && pathname !== '/login' && (
          <div className="mb-12 text-center">
            <div className="bg-white border-2 border-black p-8 rounded-none">
              <div className="flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-black mr-2" />
                <h3 className="text-sm sm:text-2xl font-bold text-black">Get clear on your finances</h3>
              </div>
              <p className="text-xs sm:text-lg text-gray-600 mb-6">Take control of your financial future with Delphina</p>
              <Link
                ref={buttonRef}
                href="/login"
                onClick={() => trackCTAClick('primary_footer')}
                className={`px-8 py-4 text-lg border border-transparent text-base font-semibold rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 transform hover:scale-105 transition-all duration-200 inline-flex items-center ${isButtonVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
                  }`}
                style={{
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
                }}
              >
                Get started now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        )}

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-6 gap-8">
            {/* Company Column */}
            <div className="col-span-1">
              <h3 className="text-lg font-bold mb-4 text-white">{footerColumns[0].title}</h3>
              <ul className="space-y-2">
                {footerColumns[0].links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      onClick={() => trackFooterClick('company', link.text)}
                      className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Product Column */}
            <div className="col-span-1">
              <h3 className="text-lg font-bold mb-4 text-white">{footerColumns[1].title}</h3>
              <ul className="space-y-2">
                {footerColumns[1].links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      onClick={() => trackFooterClick('product', link.text)}
                      className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Help & Support Column */}
            <div className="col-span-1">
              <h3 className="text-lg font-bold mb-4 text-white">{footerColumns[2].title}</h3>
              <ul className="space-y-2">
                {footerColumns[2].links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {link.external ? (
                      <a
                        href={link.href}
                        onClick={() => trackFooterClick('help_support', link.text)}
                        className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.text}
                      </a>
                    ) : (
                      <Link
                        href={link.href}
                        onClick={() => trackFooterClick('help_support', link.text)}
                        className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                      >
                        {link.text}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Calculators Column */}
            <div className="col-span-1">
              <h3 className="text-lg font-bold mb-4 text-white">{footerColumns[3].title}</h3>
              <ul className="space-y-2">
                {footerColumns[3].links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      onClick={() => trackFooterClick('calculators', link.text)}
                      className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Guides Column */}
            <div className="col-span-1">
              <h3 className="text-lg font-bold mb-4 text-white">{footerColumns[4].title}</h3>
              <ul className="space-y-2">
                {footerColumns[4].links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      onClick={() => trackFooterClick('guides', link.text)}
                      className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Column */}
            <div className="col-span-1">
              <h3 className="text-lg font-bold mb-4 text-white">{footerColumns[5].title}</h3>
              <ul className="space-y-2">
                {footerColumns[5].links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link
                      href={link.href}
                      onClick={() => trackFooterClick('legal', link.text)}
                      className="text-sm text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      {link.text}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Mobile Layout - Hidden by default */}
        <div className="lg:hidden">
          {!showFooterLinks ? (
            <div className="text-center mb-8">
              <button
                onClick={() => setShowFooterLinks(true)}
                className="px-6 py-3 bg-positive-600 hover:bg-positive-700 text-white text-sm font-bold rounded-lg transition-all duration-200 inline-flex items-center"
              >
                View footer links
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-6 pb-4">
                <button
                  onClick={() => setShowFooterLinks(false)}
                  className="flex items-center justify-center w-full text-center mb-6 text-gray-400 hover:text-white transition-colors"
                >
                  <h3 className="text-lg font-bold">Footer Links</h3>
                  <ChevronUp className="w-5 h-5 ml-2" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {footerColumns.map((column, index) => (
                  <div key={index} className="border-b border-gray-700 pb-4">
                    <h4 className="text-base font-bold text-white mb-3">{column.title}</h4>
                    <ul className="space-y-2">
                      {column.links.map((link, linkIndex) => (
                        <li key={linkIndex}>
                          {link.external ? (
                            <a
                              href={link.href}
                              onClick={() => trackFooterClick(column.title.toLowerCase().replace(' & ', '_'), link.text)}
                              className="text-sm text-gray-300 hover:text-white transition-colors duration-200 block py-1"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {link.text}
                            </a>
                          ) : (
                            <Link
                              href={link.href}
                              onClick={() => trackFooterClick(column.title.toLowerCase().replace(' & ', '_'), link.text)}
                              className="text-sm text-gray-300 hover:text-white transition-colors duration-200 block py-1"
                            >
                              {link.text}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Daily Quote */}
        <div className="mt-12 mb-8 text-center">
          <blockquote
            className="max-w-4xl mx-auto cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleNextQuote}
          >
            <p
              className="text-xl md:text-2xl text-gray-200 font-light leading-relaxed mb-3"
              dangerouslySetInnerHTML={{ __html: `"${quotes[currentQuoteIndex].quote}"` }}
            />
            <cite className="text-sm text-gray-400 font-normal not-italic">
              - {quotes[currentQuoteIndex].author}
            </cite>
          </blockquote>
        </div>

        {/* UK Market Optimisation */}
        <div className="mt-8 pt-8 border-t border-gray-700">
          <div className="bg-gray-900 border-2 border-gray-700 p-4 rounded-none">
            <p className="text-center text-sm text-gray-300">
              <span className="font-bold">{currentBanner.title}:</span> {currentBanner.description}
            </p>
            <div className="text-center mt-2">
              <Link
                href={currentBanner.href}
                onClick={() => trackFooterClick('uk_market', currentBanner.tracking)}
                className="text-positive-400 hover:text-positive-300 text-sm font-bold"
              >
                Learn more <ArrowRight className="w-4 h-4 ml-1 inline" />
              </Link>
            </div>
          </div>
        </div>

        {/* Personal Coaching Session Banner */}
        <div className="mt-6">
          <div className="bg-positive-900 border-2 border-positive-600 p-4 rounded-none">
            <div className="flex items-center justify-center mb-2">
              <Users className="w-5 h-5 text-positive-300 mr-2" />
              <p className="text-center text-sm text-white font-bold">
                Personal Financial Coaching Available
              </p>
            </div>
            <p className="text-center text-sm text-positive-100 mb-3">
              Get 1:1 guidance from expert financial coaches who work with your actual numbers
            </p>
            <div className="text-center">
              <Link
                ref={coachingButtonRef}
                href="/coaching"
                onClick={() => trackFooterClick('personal_coaching', 'coaching_session_banner')}
                className={`inline-flex items-center px-4 py-2 bg-positive-600 hover:bg-positive-700 text-white text-sm font-bold border border-positive-600 rounded transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-warning-400 focus-visible:ring-offset-2 focus-visible:ring-offset-positive-900 ${isCoachingButtonVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                  }`}
                style={{
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
                }}
              >
                Book a Session
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>

          


        </div>

        {/* Social Media Links */}
        <div className="mt-8 pt-8 border-t border-gray-700">
          <div className="text-center mb-6">
            <h4 className="text-lg font-bold text-white mb-4">Follow us for more</h4>
            <div className="flex justify-center space-x-6">
              <a
                href="https://instagram.com/delphina_money"
                onClick={() => trackFooterClick('social', 'instagram')}
                className="text-gray-400 hover:text-white transition-colors duration-200"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
              <a
                href="https://www.linkedin.com/company/delphina-money"
                onClick={() => trackFooterClick('social', 'linkedin')}
                className="text-gray-400 hover:text-white transition-colors duration-200"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="w-6 h-6" />
              </a>
              <a
                href="https://x.com/delphina_money"
                onClick={() => trackFooterClick('social', 'x')}
                className="text-gray-400 hover:text-white opacity-60 hover:opacity-100 transition-opacity transition-colors duration-200"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on X"
              >
                <img src="/icons/x-logo.png" alt="X" className="w-6 h-6" />
              </a>
              <a
                href="https://www.youtube.com/@Delphina_money/shorts"
                onClick={() => trackFooterClick('social', 'youtube')}
                className="text-gray-400 hover:text-white transition-colors duration-200"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on YouTube"
              >
                <Youtube className="w-6 h-6" />
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-gray-700 text-center">
          <p className="text-center text-sm text-gray-400 mt-4 mb-2">🇬🇧 Made with love in Hampshire, UK 🇬🇧</p>
          <p className="text-sm text-gray-400 mb-2">
            © {currentYear} Minith Labs Ltd. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 max-w-4xl mx-auto px-4">
            Your data is secure and processed in accordance with our <Link href="/privacy" className="text-positive-400 hover:text-positive-300 underline">Privacy Policy</Link>. We are registered with the Information Commissioner's Office (ICO) under the number <Link href="https://ico.org.uk/ESDWebPages/Entry/ZC069960" target="_blank" className="text-positive-400 hover:text-positive-300 underline">ZC069960</Link>. Minith Labs is not regulated by the FCA as Minith Labs is not providing Financial Advice. Minith Labs Ltd is registered in England and Wales, company number <Link href="https://find-and-update.company-information.service.gov.uk/company/16632010" target="_blank" className="text-positive-400 hover:text-positive-300 underline">16632010</Link>. Delphina and Delphina Money are trading names of Minith Labs Ltd.



          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
